/**
 * Theme Section Registry
 * Maps section types to their React components
 */

import HeroBanner from './HeroBanner';
import HeroSlider from './HeroSlider';
import FeaturedProducts from './FeaturedProducts';
import AllProducts from './AllProducts';
import CategoriesGrid from './CategoriesGrid';
import Testimonials from './Testimonials';
import Newsletter from './Newsletter';
import TextImage from './TextImage';
import VideoBanner from './VideoBanner';
import TrustBadges from './TrustBadges';
import AnnouncementBar from './AnnouncementBar';
import AdSection, { AD_PLACEMENTS } from './AdSection';
import VideoPlayer, { detectVideoType, extractYouTubeId, extractVimeoId } from './VideoPlayer';

/**
 * Section registry - maps section type to component
 */
export const sectionRegistry = {
  'hero-banner': HeroBanner,
  'hero-slider': HeroSlider,
  'featured-products': FeaturedProducts,
  'all-products': AllProducts,
  'categories-grid': CategoriesGrid,
  'testimonials': Testimonials,
  'newsletter': Newsletter,
  'text-image': TextImage,
  'video-banner': VideoBanner,
  'trust-badges': TrustBadges,
  'announcement-bar': AnnouncementBar,
  'ad-placement': AdSection,
};

/**
 * Section metadata for builder UI
 */
export const sectionMeta = {
  'hero-banner': {
    name: 'Hero Banner',
    description: 'Full-width hero with title, subtitle, and call-to-action',
    icon: 'layout',
    category: 'hero',
  },
  'hero-slider': {
    name: 'Hero Slider',
    description: 'Carousel of hero images with autoplay',
    icon: 'images',
    category: 'hero',
  },
  'featured-products': {
    name: 'Featured Products',
    description: 'Grid of products with customizable columns',
    icon: 'grid',
    category: 'products',
  },
  'all-products': {
    name: 'All Products',
    description: 'Full product catalog with search, filters, and pagination',
    icon: 'grid',
    category: 'products',
  },
  'categories-grid': {
    name: 'Categories Grid',
    description: 'Category cards with images and labels',
    icon: 'folder',
    category: 'navigation',
  },
  'testimonials': {
    name: 'Testimonials',
    description: 'Customer reviews and testimonials',
    icon: 'quote',
    category: 'social-proof',
  },
  'newsletter': {
    name: 'Newsletter',
    description: 'Email signup form with customizable text',
    icon: 'mail',
    category: 'marketing',
  },
  'text-image': {
    name: 'Text + Image',
    description: 'Split content block with text and image',
    icon: 'columns',
    category: 'content',
  },
  'video-banner': {
    name: 'Video Banner',
    description: 'Full-width video with overlay and CTA (YouTube, Vimeo, or self-hosted)',
    icon: 'play',
    category: 'media',
  },
  'trust-badges': {
    name: 'Trust Badges',
    description: 'Payment, shipping, and security icons',
    icon: 'shield',
    category: 'social-proof',
  },
  'announcement-bar': {
    name: 'Announcement Bar',
    description: 'Top banner for promotions and notices',
    icon: 'megaphone',
    category: 'marketing',
  },
  'ad-placement': {
    name: 'Ad Placement',
    description: 'Display ads from your ad campaigns',
    icon: 'ad',
    category: 'monetization',
    premium: true,
  },
};

/**
 * Default section settings by type
 */
export const defaultSectionSettings = {
  'hero-banner': {
    height: '500px',
    overlayOpacity: 0.3,
    textAlign: 'center',
    verticalAlign: 'center',
  },
  'hero-slider': {
    height: '600px',
    autoplay: true,
    interval: 5000,
    showDots: true,
    showArrows: true,
    overlayOpacity: 0.2,
  },
  'featured-products': {
    count: 8,
    columns: 4,
    showPrice: true,
    showQuickAdd: false,
  },
  'all-products': {
    productsPerPage: 12,
    defaultView: 'grid',
    showControls: true,
  },
  'categories-grid': {
    columns: 4,
    showCount: true,
    imageStyle: 'square',
  },
  'testimonials': {
    layout: 'carousel',
    showPhotos: true,
    showRating: true,
    maxItems: 6,
  },
  'newsletter': {
    layout: 'horizontal',
    backgroundColor: '#f9fafb',
  },
  'text-image': {
    imagePosition: 'right',
    textAlign: 'left',
    backgroundColor: '#ffffff',
  },
  'video-banner': {
    height: '500px',
    autoplay: true,
    muted: true,
    loop: true,
    showControls: false,
    overlayOpacity: 0.3,
    objectFit: 'cover',
    textAlign: 'center',
    verticalAlign: 'center',
  },
  'trust-badges': {
    layout: 'horizontal',
    backgroundColor: '#ffffff',
  },
  'announcement-bar': {
    backgroundColor: '#1a1a2e',
    textColor: '#ffffff',
    dismissible: true,
  },
  'ad-placement': {
    placement: 'HOMEPAGE_BANNER',
    showAdvertiseBlurb: true,
    maxAds: 1,
    backgroundColor: 'transparent',
    padding: '20px 0',
  },
};

/**
 * Generate unique section ID
 */
export function generateSectionId(type) {
  return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new section with default settings
 */
export function createSection(type) {
  return {
    id: generateSectionId(type),
    type,
    settings: { ...defaultSectionSettings[type] },
    content: {},
  };
}

export {
  HeroBanner,
  HeroSlider,
  FeaturedProducts,
  CategoriesGrid,
  Testimonials,
  Newsletter,
  TextImage,
  VideoBanner,
  TrustBadges,
  AnnouncementBar,
  AdSection,
  AD_PLACEMENTS,
  VideoPlayer,
  detectVideoType,
  extractYouTubeId,
  extractVimeoId,
};
